import React, { useState } from 'react';
import { Badge, IconButton, Snackbar, Surface } from 'react-native-paper';
import { Image, Linking, TouchableOpacity, View } from 'react-native';
import { useNavigation, useNavigationState } from '@react-navigation/native';
import { MaterialIcons } from '@expo/vector-icons';
import Environment from '../../models/Environment';

import { DatImageUtility } from '../../services';
import { Images, MAX_SCREEN_WIDTH } from '../../models';
import { useAuth, useThemeContext } from '../../contexts';
import { GradientBar } from './GradientBar';
import { NavigationLink } from './NavigationLink';
import { SCREEN_NAMES } from '../routes';
import { MobileMenu } from './header/MobileMenu';
import { DeviceType, useResponsive } from '../hooks';
import { AppNavigation } from './AppNavigation';
import { AccountMenu } from './AccountMenu';

const LAB_LOGO_WIDTH = 202;
const LAB_LOGO_HEIGHT = 92;

export const Header = () => {
  const { session, authData, countItemsInCart, showSnack, snackMessage, hideSnack, setCart, signOut } = useAuth();
  const { theme } = useThemeContext();
  const navigation = useNavigation();
  const navigationState = useNavigationState((state) => state);
  const { deviceType } = useResponsive();

  const isSignInVisible = !authData && SCREEN_NAMES.AUTH !== navigationState.routes[navigationState.index].name;
  const guestCheckout = session?.appConfig.guestCheckoutEnabled;

  const [displayMobileMenu, setDisplayMobileMenu] = useState(false);

  const onLayoutChanged = () => {
    if (deviceType === DeviceType.MOBILE || deviceType === DeviceType.TABLET) {
      setDisplayMobileMenu(true);
    } else {
      setDisplayMobileMenu(false);
    }
  };

  const onNavigateToScreen = (screen: string) => {
    // @ts-ignore
    navigation.navigate(screen);
  };

  const onNavigateToLocationsUrl = async () => {
    if (session?.appConfig?.customLabLocationsUrl) {
      await Linking.openURL(session?.appConfig?.customLabLocationsUrl as string);
    } else {
      onNavigateToScreen(SCREEN_NAMES.LAB_LOCATIONS as string);
    }
  };

  const onNavigateToViewResultsUrl = async () => {
    if (session?.appConfig?.viewResultsUrl) {
      await Linking.openURL(session?.appConfig?.viewResultsUrl as string);
    } else {
      await Linking.openURL(Environment.apiBaseUrl as string);
    }
  };

  const onSignOut = () => {
    signOut();
    setCart(undefined);
    onNavigateToScreen(SCREEN_NAMES.AUTH);
  };

  return (
    <View
      style={{
        flex: 1,
        flexWrap: 'wrap',
        width: '100%',
        maxWidth: MAX_SCREEN_WIDTH,
        alignSelf: 'center',
      }}
      onLayout={onLayoutChanged}
    >
      <GradientBar />
      <Surface
        style={{
          flex: 1,
          flexDirection: 'row',
          alignItems: 'center',
          width: '100%',
          minWidth: 260,
        }}
      >
        <TouchableOpacity onPress={() => onNavigateToScreen(SCREEN_NAMES.BROWSE)}>
          <Image
            source={{ uri: DatImageUtility.getImageUrl(session?.lab.id, 'logo.png') }}
            defaultSource={Images.placeholder}
            style={[
              {
                width: LAB_LOGO_WIDTH,
                height: LAB_LOGO_HEIGHT,
                marginLeft: 10,
              },
            ]}
            resizeMode={'contain'}
          />
        </TouchableOpacity>
        <View
          style={{
            flex: 3,
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {!displayMobileMenu && (
            <AppNavigation
              onNavigateToScreen={onNavigateToScreen}
              onNavigateToLocationsUrl={onNavigateToLocationsUrl}
              onNavigateToViewResultsUrl={onNavigateToViewResultsUrl}
            />
          )}
        </View>
        <View
          style={{
            flex: 1,
            flexDirection: 'row-reverse',
            flexWrap: 'wrap',
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            {authData && !guestCheckout && <AccountMenu displayMobileMenu={displayMobileMenu} onSignOut={onSignOut} />}
            {!displayMobileMenu && !guestCheckout && (
              <NavigationLink
                screen={SCREEN_NAMES.AUTH}
                label={'Sign In'}
                onNavigate={onNavigateToScreen}
                visible={isSignInVisible}
                iconPosition={'left'}
                left={<MaterialIcons name={'login'} size={24} color={theme.colors.primary} />}
              />
            )}
            <NavigationLink
              screen={SCREEN_NAMES.CART}
              label={'Cart'}
              onNavigate={onNavigateToScreen}
              visible={!displayMobileMenu}
              icon={<MaterialIcons name={'shopping-cart'} size={24} color={theme.colors.primary} />}
              iconPosition={'left'}
              left={
                <View>
                  <Badge
                    style={{ fontSize: 15, position: 'absolute', top: -21, left: -11 }}
                    visible={countItemsInCart() > 0}
                    selectionColor={theme.colors.secondary}
                  >
                    {countItemsInCart()}
                  </Badge>
                </View>
              }
            />
            {displayMobileMenu && (
              <MobileMenu
                countItemsInCart={countItemsInCart()}
                onNavigateToLocationsUrl={onNavigateToLocationsUrl}
                onNavigateToViewResultsUrl={onNavigateToViewResultsUrl}
              />
            )}
          </View>
        </View>
      </Surface>
      <Snackbar
        visible={showSnack}
        onDismiss={hideSnack}
        action={<IconButton icon={'close'} size={16} iconColor={theme.colors.surface} onPress={hideSnack} />}
      >
        {snackMessage}
      </Snackbar>
    </View>
  );
};
